import {
  CheckboxList,
  TagFilterBar,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";

type Option = { id: string; label: string };

interface DxCodeRowTagFilterBarProps {
  options: readonly Option[];
  value: string[];
  onChange: (selectedIds: string[]) => void;
  ariaLabel: string;
}

const DxCodeRowTagFilterBar = ({
  options,
  value,
  onChange,
  ariaLabel,
}: DxCodeRowTagFilterBarProps) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const tags = useMemo(
    () =>
      value
        .map((id) => options.find((o) => o.id === id)?.label ?? id)
        .filter(Boolean),
    [value, options],
  );

  const items = useMemo(
    () =>
      options.map((o) => ({
        id: o.id,
        label: o.label,
        checked: value.includes(o.id),
      })),
    [value, options],
  );

  return (
    <div className="relative h-full w-full min-w-0" ref={wrapRef}>
      <TagFilterBar
        tags={tags}
        className="dx-code-row-tag-filter-bar h-full"
        closable
        showDropdown
        singleLineScrollable
        onTagRemove={(label) => {
          const opt = options.find((o) => o.label === label);
          onChange(value.filter((id) => id !== (opt?.id ?? label)));
        }}
        onDropdownClick={() => setOpen((o) => !o)}
        dropdownAriaLabel={ariaLabel}
      />
      {open && (
        <div
          className="absolute right-0 z-50 mt-1 max-h-60 min-w-[160px] overflow-auto rounded border border-[#e5e7eb] bg-white p-2 shadow-md"
          role="listbox"
          aria-label={ariaLabel}
        >
          <CheckboxList
            items={items}
            showSelectAll={false}
            ariaLabel={ariaLabel}
            onSelectionChange={(newItems) => {
              onChange(newItems.filter((i) => i.checked).map((i) => i.id));
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DxCodeRowTagFilterBar;
